import React, { useEffect, useRef } from 'react';
import { CheckIcon } from '@radix-ui/react-icons';
import { cn } from '../../utils/cn';
import { Button } from '../common/Button';
import { NODE_COLORS } from './palette';

const MAX_TEXT = 200;
const MAX_NOTE = 2000;

const NodeEditor = ({ node, isRoot, onChange, onColor, onToggleDone, onClose }) => {
  const textRef = useRef(null);

  useEffect(() => {
    textRef.current?.focus();
    textRef.current?.select();
  }, [node.id]);

  const handleTextKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      onClose();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
    }
  };

  return (
    <div
      role="dialog"
      aria-label="Edit idea"
      onKeyDown={handleKeyDown}
      className="absolute left-3 right-3 top-3 z-20 space-y-3 rounded-2xl border border-border bg-background/95 p-3 shadow-xl backdrop-blur md:left-auto md:right-4 md:top-4 md:w-80"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {isRoot ? 'Central idea' : 'Idea'}
        </span>
        <Button variant="ghost" size="sm" onClick={onClose} className="-mr-1 h-8 gap-1 px-2 text-primary">
          <CheckIcon className="h-4 w-4" />
          Done
        </Button>
      </div>

      <textarea
        ref={textRef}
        rows={2}
        maxLength={MAX_TEXT}
        value={node.text}
        onChange={(event) => onChange({ text: event.target.value })}
        onKeyDown={handleTextKeyDown}
        placeholder="What's the idea?"
        className="w-full resize-none rounded-lg border border-input bg-transparent px-3 py-2 text-base font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />

      <textarea
        rows={3}
        maxLength={MAX_NOTE}
        value={node.note}
        onChange={(event) => onChange({ note: event.target.value })}
        placeholder="Notes, links, findings…"
        className="w-full resize-none rounded-lg border border-input bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />

      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2" role="radiogroup" aria-label="Color">
          {NODE_COLORS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              role="radio"
              aria-checked={node.color === key}
              aria-label={isRoot ? label : `${label} (whole branch)`}
              title={isRoot ? label : `${label} (whole branch)`}
              onClick={() => onColor(key)}
              className={cn(
                `mm-c-${key} mm-swatch h-6 w-6 rounded-full ring-offset-2 ring-offset-background transition-transform hover:scale-110`,
                node.color === key && 'ring-2 ring-foreground/70'
              )}
            />
          ))}
        </div>

        <button
          type="button"
          role="switch"
          aria-checked={node.done}
          onClick={onToggleDone}
          className={cn(
            'inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors',
            node.done
              ? 'border-transparent bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
              : 'border-input text-muted-foreground hover:text-foreground'
          )}
        >
          <CheckIcon className="h-3.5 w-3.5" />
          {node.done ? 'Done' : 'Mark done'}
        </button>
      </div>

      <p className="text-right text-[11px] text-muted-foreground">Enter to finish · Shift+Enter for a new line</p>
    </div>
  );
};

export default NodeEditor;
